import { Camera, Car, Receipt, DollarSign, Download, Check, ArrowRight, Zap, Phone, Sparkles } from 'lucide-react';
import { useEffect } from 'react';
import { T, LogoMark, Wordmark } from '../theme';
import GlobalFooterBar from './GlobalFooterBar';
import { setPageMeta } from '../utils/pageMeta';

const PLATFORMS = [
  { name: 'Uber', color: '#000' },
  { name: 'Lyft', color: '#FF00BF' },
  { name: 'DoorDash', color: '#FF3008' },
  { name: 'Instacart', color: '#43B02A' },
  { name: 'Uber Eats', color: '#06C167' },
  { name: 'Grubhub', color: '#F63440' },
  { name: 'TaskRabbit', color: '#6A4DBC' },
  { name: 'Fiverr', color: '#1DBF73' },
  { name: 'Upwork', color: '#14A800' },
];

const DEDUCTIONS = [
  { icon: Car,     label: 'Mileage',         tip: 'Track miles and auto-calculate at the IRS rate ($0.725/mi)' },
  { icon: Phone,   label: 'Phone & Data',     tip: 'Your work phone line and data plan are deductible' },
  { icon: Receipt, label: 'Supplies',         tip: 'Bags, coolers, insulated bags, cleaning supplies' },
  { icon: DollarSign, label: 'Platform Fees', tip: 'Service fees and commissions taken by platforms' },
];

const STEPS = [
  {
    num: '1',
    title: 'Snap the receipt',
    desc: 'Take a photo with your phone — AI instantly reads vendor, amount, date, and category. No typing.',
  },
  {
    num: '2',
    title: 'Log mileage',
    desc: 'Enter your miles and origin/destination. The IRS rate is pre-filled. Total calculates automatically.',
  },
  {
    num: '3',
    title: 'Export at tax time',
    desc: 'Download a tax-ready report for your accountant, or export to CSV/Excel to file yourself.',
  },
];

const TESTIMONIALS = [
  {
    quote: "I used to lose hundreds in deductions every year because I couldn't keep track of receipts. Now I scan them on the spot and export everything at tax time in minutes.",
    name: 'Marcus T.',
    role: 'Full-time DoorDash driver',
  },
  {
    quote: "Between Uber and Instacart, tracking mileage separately was a nightmare. One app, one report, done.",
    name: 'Priya S.',
    role: 'Multi-platform gig worker',
  },
];

function GetStartedButton({ onSignUp, label = 'Get Started — It\'s Free', size = 'lg' }) {
  const lg = size === 'lg';
  return (
    <button
      onClick={onSignUp}
      style={{
        padding: lg ? '15px 32px' : '12px 24px',
        borderRadius: 14, border: 'none',
        background: T.gradTeal, color: '#fff',
        fontSize: lg ? 16 : 14, fontWeight: 800,
        cursor: 'pointer', boxShadow: '0 6px 24px rgba(5,196,176,0.35)',
        display: 'inline-flex', alignItems: 'center', gap: 8,
      }}
    >
      {label} <ArrowRight size={lg ? 18 : 15} />
    </button>
  );
}

function GigLandingPage({ onSignUp, onSignIn, onSkip, onPricing }) {
  useEffect(() => {
    setPageMeta({
      title: 'Expense Tracking for Gig Workers',
      description: 'Track every mile, phone charge, and platform fee. IRS-ready tax reports for Uber, DoorDash, Instacart drivers and more. Free to start.',
      url: 'https://myspend.info/?page=gig',
    });
  }, []);
  return (
    <div style={{ fontFamily: T.fontBody, background: T.appBg, minHeight: '100vh', paddingBottom: 36 }}>

      {/* ── Nav ─────────────────────────────────────────────────────── */}
      <nav style={{
        background: T.navy, padding: '0 24px', height: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
        boxShadow: '0 2px 16px rgba(0,0,0,0.25)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LogoMark size={28} />
          <Wordmark size={18} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={onPricing} style={{
          background: 'none', border: 'none',
          color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600,
          padding: '8px 12px', cursor: 'pointer',
        }}>
          Pricing
        </button>
          <button onClick={onSignIn} style={{
            background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 10,
            color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 700,
            padding: '8px 16px', cursor: 'pointer',
          }}>
            Sign In
          </button>
          <button onClick={onSignUp} style={{
            background: T.gradTeal, border: 'none', borderRadius: 10,
            color: '#fff', fontSize: 13, fontWeight: 700,
            padding: '8px 16px', cursor: 'pointer',
            boxShadow: '0 3px 12px rgba(5,196,176,0.3)',
          }}>
            Get Started Free
          </button>
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section style={{ background: T.gradNavy, padding: '72px 24px 80px', textAlign: 'center' }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'rgba(5,196,176,0.15)', border: '1px solid rgba(5,196,176,0.3)',
            borderRadius: 20, padding: '5px 14px', marginBottom: 24,
          }}>
            <Zap size={13} color={T.tealBright} />
            <span style={{ fontSize: 12, fontWeight: 700, color: T.tealBright, letterSpacing: 0.5 }}>
              AI-POWERED FINANCES FOR GIG WORKERS
            </span>
          </div>

          <h1 style={{
            fontSize: 'clamp(30px, 6vw, 52px)', fontWeight: 900,
            color: '#fff', margin: '0 0 20px', lineHeight: 1.15, letterSpacing: -1,
          }}>
            Keep every deduction.<br />
            <span style={{ color: T.tealBright }}>Stop leaving money behind.</span>
          </h1>

          <p style={{
            fontSize: 18, color: 'rgba(255,255,255,0.65)', margin: '0 0 16px',
            lineHeight: 1.65, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto',
          }}>
            Snap receipts between rides, log mileage with one tap, and export a tax-ready report when April comes.
            Built for drivers, dashers, and independent contractors.
          </p>

          <p style={{ fontSize: 15, fontWeight: 800, color: T.tealBright, margin: '0 0 36px', letterSpacing: 0.3 }}>
            Free to use. No credit card. No commitment.
          </p>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <GetStartedButton onSignUp={onSignUp} label="Get Started Now" />
            <button onClick={onSignIn} style={{
              padding: '15px 32px', borderRadius: 14,
              border: '1.5px solid rgba(255,255,255,0.2)',
              background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.85)',
              fontSize: 16, fontWeight: 700, cursor: 'pointer',
            }}>
              Sign In
            </button>
          </div>

        </div>
      </section>

      {/* ── Platform strip ───────────────────────────────────────────── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #E5E7EB', padding: '20px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 14 }}>
          Works with income from any platform
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 20px', justifyContent: 'center', alignItems: 'center' }}>
          {PLATFORMS.map(p => (
            <span key={p.name} style={{ fontSize: 13, fontWeight: 800, color: p.color, opacity: 0.8 }}>{p.name}</span>
          ))}
        </div>
      </div>

      {/* ── The real cost of missed deductions ───────────────────────── */}
      <section style={{ padding: '64px 24px', maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
        <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 900, color: T.navy, margin: '0 0 16px' }}>
          The average gig worker misses <span style={{ color: T.tealDeep }}>$2,000+</span> in deductions every year
        </h2>
        <p style={{ fontSize: 16, color: '#6B7280', lineHeight: 1.65, maxWidth: 520, margin: '0 auto 40px' }}>
          Mileage, supplies, phone plans, platform fees — they add up fast.
          But only if you actually tracked them. MySpend.info makes tracking effortless so you keep what you earned.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          {['Mileage auto-calculated', 'IRS-ready export', 'AI receipt scanning', '100% free to start'].map(item => (
            <div key={item} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: T.tealPale, borderRadius: 20, padding: '6px 14px',
            }}>
              <Check size={13} color={T.tealDeep} strokeWidth={3} />
              <span style={{ fontSize: 13, fontWeight: 700, color: T.tealDeep }}>{item}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Top deductions ───────────────────────────────────────────── */}
      <section style={{ background: '#fff', padding: '64px 24px' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 900, color: T.navy, textAlign: 'center', margin: '0 0 8px' }}>
            Track every deduction in one place
          </h2>
          <p style={{ fontSize: 15, color: '#9CA3AF', textAlign: 'center', marginBottom: 40 }}>
            MySpend.info has built-in support for the expenses that matter most to gig workers.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {DEDUCTIONS.map(({ icon: Icon, label, tip }) => (
              <div key={label} style={{
                display: 'flex', alignItems: 'flex-start', gap: 14,
                background: T.appBg, borderRadius: 14, padding: '18px 20px',
                border: '1.5px solid #E5E7EB',
              }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                  background: T.tealPale, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={20} color={T.tealDeep} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: T.navy, marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>{tip}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────────────── */}
      <section style={{ padding: '64px 24px', maxWidth: 700, margin: '0 auto' }}>
        <h2 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 900, color: T.navy, textAlign: 'center', margin: '0 0 48px' }}>
          As quick as the gig itself
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
          {STEPS.map((step, i) => (
            <div key={i} style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
              <div style={{
                width: 44, height: 44, borderRadius: 22, flexShrink: 0,
                background: T.gradTeal, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, fontWeight: 900, color: '#fff',
                boxShadow: '0 4px 14px rgba(5,196,176,0.35)',
              }}>
                {step.num}
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 800, color: T.navy, marginBottom: 6 }}>{step.title}</div>
                <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6 }}>{step.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── AI Bookkeeper callout ───────────────────────────────────── */}
      <section style={{ background: T.tealPale, padding: '64px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 20, background: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <Sparkles size={30} color={T.tealDeep} />
          </div>
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 900, color: T.navy, margin: '0 0 16px' }}>
            Tax questions answered in seconds
          </h2>
          <p style={{ fontSize: 16, color: '#4B5563', lineHeight: 1.65, marginBottom: 28 }}>
            The AI Bookkeeper knows your data. Ask anything about your deductions and earnings — in plain English, without touching a spreadsheet.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left', marginBottom: 32 }}>
            {[
              '"What\'s my total deduction amount so far this year?"',
              '"Show all my platform fee expenses by month"',
              '"Which category is my biggest deduction?"',
              '"How many receipts did I scan in April?"',
            ].map(q => (
              <div key={q} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  <Check size={11} color={T.tealDeep} />
                </div>
                <span style={{ fontSize: 14, color: '#374151', fontStyle: 'italic' }}>{q}</span>
              </div>
            ))}
          </div>
          <GetStartedButton onSignUp={onSignUp} label="Try It Free" />
        </div>
      </section>

      {/* ── AI Scanning callout ──────────────────────────────────────── */}
      <section style={{
        background: T.gradNavy, padding: '64px 24px', textAlign: 'center',
      }}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 20, background: 'rgba(5,196,176,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
            border: '1px solid rgba(5,196,176,0.3)',
          }}>
            <Camera size={30} color={T.tealBright} />
          </div>
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 900, color: '#fff', margin: '0 0 16px' }}>
            Scan a receipt in 5 seconds
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.65)', lineHeight: 1.65, marginBottom: 32 }}>
            Stop at a gas station, snap the receipt, and you're back on the road.
            AI reads the vendor, amount, date, and category automatically.
            No app switching. No manual entry.
          </p>
          <GetStartedButton onSignUp={onSignUp} label="Get Started Now" />
        </div>
      </section>

      {/* ── Testimonials ─────────────────────────────────────────────── */}
      <section style={{ background: '#fff', padding: '64px 24px' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 30px)', fontWeight: 900, color: T.navy, textAlign: 'center', margin: '0 0 40px' }}>
            What gig workers are saying
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} style={{
                background: T.appBg, borderRadius: 16, padding: '24px',
                border: '1.5px solid #E5E7EB',
              }}>
                <div style={{ fontSize: 28, color: T.tealBright, fontWeight: 900, lineHeight: 1, marginBottom: 12 }}>"</div>
                <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.65, margin: '0 0 16px', fontStyle: 'italic' }}>
                  {t.quote}
                </p>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>{t.name}</div>
                <div style={{ fontSize: 12, color: '#9CA3AF' }}>{t.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────────────── */}
      <section style={{ padding: '72px 24px', textAlign: 'center', background: T.gradNavy }}>
        <div style={{ maxWidth: 520, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(24px, 5vw, 40px)', fontWeight: 900, color: '#fff', margin: '0 0 16px', lineHeight: 1.2 }}>
            Start tracking.<br />
            <span style={{ color: T.tealBright }}>Keep more of what you earn.</span>
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.6)', margin: '0 0 32px', lineHeight: 1.6 }}>
            Free forever for basic tracking. No credit card required.
          </p>
          <GetStartedButton onSignUp={onSignUp} label="Get Started Now — It's Free" />
        </div>
      </section>

      <GlobalFooterBar />
    </div>
  );
}

export default GigLandingPage;
